import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";
import PtfButton from "@/components/PtfButton";
import { Reveal, MaskText } from "@/components/motion/Reveal";
import { api, formatApiError } from "@/lib/api";
import { trackMetaEvent } from "@/lib/meta";
import { VOLUNTEER_CATEGORIES, VOLUNTEER_LEVELS, VOLUNTEER_STEPS, COMMITMENTS } from "@/lib/learning";
import { useActiveMembership } from "@/hooks/useActiveMembership";
import MembershipGateNotice from "@/components/membership/MembershipGateNotice";

const inputCls = "w-full bg-transparent border-b border-ptf-primary/25 py-2.5 text-sm outline-none transition-colors duration-300 focus:border-ptf-secondary placeholder:text-ptf-text/40 text-ptf-text";
const Eyebrow = ({ children, light }) => (
  <p className={`text-[0.7rem] uppercase tracking-[0.28em] mb-5 flex items-center gap-3 ${light ? "text-ptf-accent" : "text-ptf-primary/60"}`}>
    <span className={`h-px w-8 inline-block ${light ? "bg-ptf-accent" : "bg-ptf-primary/40"}`} />{children}
  </p>
);

function ApplicationForm() {
  const { member, hasActive, isLoading } = useActiveMembership();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", country: "India", age: "", profession: "",
    skills: "", previous_volunteering: "", field_experience: "", availability: "",
    commitment: "", preferred_location: "", preferred_programme: params.get("programme") || "", motivation: "",
    emergency_contact: "", certifications: "", portfolio_url: "", media_skills: "", languages: "",
  });
  const [areas, setAreas] = useState(() => {
    const a = params.get("area");
    return a && VOLUNTEER_CATEGORIES.some((c) => c.name === a) ? [a] : [];
  });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleArea = (a) => setAreas((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]));

  useEffect(() => {
    if (member) {
      setForm((f) => ({ ...f, name: f.name || member.name || "", email: f.email || member.email || "", phone: f.phone || member.phone || "" }));
    }
  }, [member]);

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please confirm the consent statement to submit."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/volunteer-applications", { ...form, areas, consent });
      trackMetaEvent("Lead", { content_name: "Volunteer Application", content_type: "volunteer" });
      setDone(data.message);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send the application.");
    } finally {
      setLoading(false);
    }
  };

  if (isLoading || !hasActive) {
    return <MembershipGateNotice member={member} isLoading={isLoading} testid="volunteer-membership-gate" />;
  }

  if (done) {
    return (
      <motion.div data-testid="volunteer-app-success" initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="py-6">
        <span className="grid place-items-center h-14 w-14 rounded-full bg-ptf-accent text-ptf-primary mb-6"><Check className="h-7 w-7" /></span>
        <h3 className="font-display uppercase text-3xl tracking-tight text-ptf-primary">Application received.</h3>
        <p className="mt-4 max-w-md text-ptf-text/75 leading-relaxed">{done}</p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="volunteer-application-form" className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input required data-testid="va-name" value={form.name} onChange={set("name")} placeholder="Full name *" className={inputCls} />
        <input required type="email" data-testid="va-email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} />
        <input data-testid="va-phone" value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} />
        <input data-testid="va-age" value={form.age} onChange={set("age")} placeholder="Age" className={inputCls} />
        <input data-testid="va-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} />
        <input data-testid="va-country" value={form.country} onChange={set("country")} placeholder="Country" className={inputCls} />
        <input data-testid="va-profession" value={form.profession} onChange={set("profession")} placeholder="Profession / background" className={inputCls} />
        <input data-testid="va-emergency" value={form.emergency_contact} onChange={set("emergency_contact")} placeholder="Emergency contact (name & phone)" className={inputCls} />
      </div>

      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55 mb-3">Areas of interest</p>
        <div className="flex flex-wrap gap-2">
          {VOLUNTEER_CATEGORIES.map((c) => (
            <button
              key={c.name}
              type="button"
              data-testid={`va-area-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => toggleArea(c.name)}
              className={`text-[0.65rem] uppercase tracking-[0.12em] px-3 py-1.5 border transition-colors ${areas.includes(c.name) ? "bg-ptf-primary text-ptf-bg border-ptf-primary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-[0.65rem] uppercase tracking-[0.2em] text-ptf-text/55 mb-3">Commitment</p>
        <div className="flex flex-wrap gap-2">
          {COMMITMENTS.map((c) => (
            <button
              key={c}
              type="button"
              data-testid={`va-commitment-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => setForm((f) => ({ ...f, commitment: f.commitment === c ? "" : c }))}
              className={`text-[0.65rem] uppercase tracking-[0.12em] px-3.5 py-2 border transition-colors ${form.commitment === c ? "bg-ptf-secondary text-ptf-bg border-ptf-secondary" : "border-ptf-primary/25 text-ptf-text/70 hover:border-ptf-primary"}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <input data-testid="va-skills" value={form.skills} onChange={set("skills")} placeholder="Skills" className={inputCls} />
        <input data-testid="va-languages" value={form.languages} onChange={set("languages")} placeholder="Languages" className={inputCls} />
        <input data-testid="va-previous" value={form.previous_volunteering} onChange={set("previous_volunteering")} placeholder="Previous volunteer experience" className={inputCls} />
        <input data-testid="va-field" value={form.field_experience} onChange={set("field_experience")} placeholder="Field experience" className={inputCls} />
        <input data-testid="va-availability" value={form.availability} onChange={set("availability")} placeholder="Availability (e.g. weekends, one month)" className={inputCls} />
        <input data-testid="va-location" value={form.preferred_location} onChange={set("preferred_location")} placeholder="Preferred location" className={inputCls} />
        <input data-testid="va-programme" value={form.preferred_programme} onChange={set("preferred_programme")} placeholder="Preferred programme (optional)" className={inputCls} />
        <input data-testid="va-certs" value={form.certifications} onChange={set("certifications")} placeholder="Relevant certifications" className={inputCls} />
        <input data-testid="va-portfolio" value={form.portfolio_url} onChange={set("portfolio_url")} placeholder="Portfolio / CV link (URL)" className={inputCls} />
        <input data-testid="va-media" value={form.media_skills} onChange={set("media_skills")} placeholder="Photo / video skills" className={inputCls} />
      </div>
      <textarea required data-testid="va-motivation" rows={3} value={form.motivation} onChange={set("motivation")} placeholder="Why do you want to volunteer? *" className={`${inputCls} resize-none`} />

      <label className="flex items-start gap-3 text-xs text-ptf-text/70 leading-relaxed cursor-pointer">
        <input type="checkbox" data-testid="va-consent" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 accent-ptf-primary" />
        I consent to Planet Trotter Foundation storing this information to process my volunteer application and contacting me about volunteering opportunities. *
      </label>
      <PtfButton type="submit" disabled={loading} data-testid="va-submit">
        {loading ? "Sending" : "Submit Application"}
      </PtfButton>
    </form>
  );
}

export default function Volunteer() {
  useEffect(() => {
    trackMetaEvent("ViewContent", { content_name: "Volunteer", content_type: "volunteer" });
  }, []);
  return (
    <>
      <Seo title="Volunteer With Planet Trotter" path="/volunteer" description="Volunteer with Planet Trotter Foundation across environment, education, communities, sports, adventure, rural tourism, research, media, digital support and agriculture. One-time to long term, remote or field." />

      <section data-testid="volunteer-hero" className="relative min-h-[80vh] flex items-end bg-ptf-primary text-ptf-bg grain overflow-hidden">
        <img src="/dawn/trees-planting.jpg" alt="Volunteers planting trees during a documented Project Dawn field day" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ptf-primary via-ptf-primary/55 to-ptf-primary/20" />
        <div className="relative mx-auto max-w-[1600px] w-full px-5 sm:px-8 lg:px-12 pb-16 md:pb-24 pt-44">
          <Eyebrow light>Volunteer with Planet Trotter</Eyebrow>
          <MaskText
            tag="h1"
            lines={["Your time can become", "field action."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight"
          />
          <Reveal delay={0.2}>
            <p className="mt-6 max-w-2xl text-ptf-bg/80 leading-relaxed">
              Join Planet Trotter programmes supporting conservation, communities,
              education, responsible tourism, outdoor learning and environmental action.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8">
              <a href="#apply" data-testid="volunteer-hero-apply" className="inline-flex items-center gap-2 bg-ptf-accent text-ptf-primary px-8 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] hover:brightness-95 transition">
                Apply to Volunteer <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal><Eyebrow>Why volunteer?</Eyebrow></Reveal>
            <MaskText
              lines={["Useful, honest", "contribution."]}
              className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
            />
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.15}>
              <p className="text-lg text-ptf-text/80 leading-relaxed">
                Planet Trotter's volunteering is not symbolic. School and village
                volunteering is documented in the Project Dawn field material, and every
                new volunteer joins real work: plantation days, school sessions,
                documentation, community events and field support.
              </p>
              <p className="mt-4 text-ptf-text/70 leading-relaxed">
                You choose your area and commitment; we match you to a real need. No
                impact numbers are promised in advance, and what you do is documented
                honestly.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="volunteer-categories" className="bg-white border-y border-ptf-primary/10 py-16 md:py-24">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>Choose your area</Eyebrow></Reveal>
          <MaskText
            lines={["Ten ways in."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-px bg-ptf-primary/10 border border-ptf-primary/10">
            {VOLUNTEER_CATEGORIES.map((c, i) => (
              <Reveal key={c.name} delay={(i % 5) * 0.04} className="h-full">
                <div data-testid={`vol-cat-${c.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="bg-white p-6 h-full">
                  <h3 className="font-display uppercase text-lg tracking-tight text-ptf-primary">{c.name}</h3>
                  <ul className="mt-3 space-y-1 text-xs text-ptf-text/65 leading-relaxed">
                    {c.programmes.map((p) => <li key={p}>{p}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="volunteer-how" className="bg-ptf-primary text-ptf-bg py-16 md:py-24 grain">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow light>How it works</Eyebrow></Reveal>
          <MaskText
            lines={["From application", "to certificate."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight"
          />
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
            {VOLUNTEER_STEPS.map((s, i) => (
              <Reveal key={s} delay={i * 0.05}>
                <div className="border-t border-ptf-bg/20 pt-4">
                  <span className="font-display text-2xl text-ptf-bg/30">{String(i + 1).padStart(2, "0")}</span>
                  <p className="mt-1 font-display uppercase text-lg tracking-tight">{s}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {VOLUNTEER_LEVELS.map((l) => (
              <div key={l.name} data-testid={`vol-level-${l.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="border border-ptf-bg/15 p-5">
                <h3 className="font-display uppercase text-lg tracking-tight text-ptf-accent">{l.name}</h3>
                <p className="mt-1.5 text-xs text-ptf-bg/70 leading-relaxed">{l.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="apply" className="bg-ptf-bg py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-5 sm:px-8 lg:px-12">
          <Reveal><Eyebrow>Volunteer application</Eyebrow></Reveal>
          <MaskText
            lines={["Tell us how you", "want to help."]}
            className="font-display font-extrabold uppercase text-4xl sm:text-5xl leading-[0.92] tracking-tight text-ptf-primary"
          />
          <div className="mt-10 bg-white border border-ptf-primary/12 p-7 md:p-10">
            <ApplicationForm />
          </div>
          <p className="mt-6 text-xs text-ptf-text/55 leading-relaxed">
            Only information needed to place you well is collected. Applications go
            through screening and orientation before any assignment; a Planet Trotter
            Volunteer Certificate can be issued after documented participation.
          </p>
        </div>
      </section>
    </>
  );
}
